/**
 * bomVersionSaveRequest.js — pure mapping from the flat rows the structure editor works with
 * (same shape as `WorkflowResourceDto`, contrato-sun.md §2) to the `BomComponentInput[]` that
 * `saveBomVersion` (N9) expects. No API calls, no React: unit-testable on its own (story 117, HU-03).
 *
 * @typedef {Object} BomStructureRow
 * @property {string} uuid
 * @property {{code: string}} [resource]
 * @property {string} [resourceCode]
 * @property {string} [resourceType]
 * @property {string} position
 * @property {number} quantity
 * @property {string} [revision]
 * @property {string} [category]
 * @property {string} [componentType]
 * @property {string} [engItem]
 * @property {string} [engRevision]
 * @property {boolean} [deleted] Rows removed by the user in this editing session (S36)
 */

const EXTENDED_COLUMNS = Array.from({ length: 12 }, (_, index) => 'extended_column' + (index + 1))

/**
 * S33/S36 (RN03.3, RN03.6): builds the `components[]` payload of a `BomVersionSaveRequest`, dropping
 * every row flagged `deleted` (S36's recursive delete) so a save never resurrects a removed
 * component or any of its descendants.
 * @param {BomStructureRow[]} rows
 * @returns {object[]} BomComponentInput[]
 */
const buildBomVersionSaveRequestComponents = (rows) => {
  return (rows || [])
    .filter((row) => !row.deleted)
    .map((row) => {
      // Sun's BomComponentInputDto: resourceCode, resourceType, position, quantity, engItem and
      // engRevision are NOT nullable — they travel as strings (empty when the user left them blank,
      // Sun then answers with its own validation literal); the rest may be null.
      const component = {
        resourceCode: (row.resource && row.resource.code) || row.resourceCode || '',
        resourceType: row.resourceType || row.resourceTypeName || '',
        position: String(row.position ?? ''),
        quantity: Number(row.quantity) || 0,
        revision: row.revision ?? null,
        category: row.category ?? null,
        componentType: row.componentType ?? null,
        engItem: String(row.engItem ?? ''),
        engRevision: String(row.engRevision ?? '')
      }
      EXTENDED_COLUMNS.forEach((field) => { component[field] = row[field] ?? null })
      return component
    })
}

/** Fields of `BomComponentInputDto` Sun declares non-nullable, in the order they are reported. */
const REQUIRED_COMPONENT_FIELDS = ['resourceCode', 'resourceType', 'position', 'quantity', 'engItem', 'engRevision']

const isBlank = (value) => value === undefined || value === null || String(value).trim() === ''

/**
 * Lists, per row, the required fields that are still empty so the save can be refused client-side
 * (instead of letting Sun answer with a 400 after the round trip). `quantity` must also be a number
 * greater than zero (E-BOM-08).
 * @param {BomStructureRow[]} rows
 * @returns {Array<{uuid: string, resourceCode: string, fields: string[]}>} empty when every row is complete
 */
const findRowsMissingRequiredFields = (rows) => {
  return (rows || [])
    .filter((row) => !row.deleted)
    .map((row) => {
      const resourceCode = (row.resource && row.resource.code) || row.resourceCode || ''
      const values = {
        resourceCode,
        resourceType: row.resourceType || row.resourceTypeName || '',
        position: row.position,
        quantity: Number(row.quantity) > 0 ? row.quantity : '',
        engItem: row.engItem,
        engRevision: row.engRevision
      }
      const fields = REQUIRED_COMPONENT_FIELDS.filter((field) => isBlank(values[field]))
      return { uuid: row.uuid || '', resourceCode, fields }
    })
    .filter((entry) => entry.fields.length > 0)
}

export { buildBomVersionSaveRequestComponents, findRowsMissingRequiredFields, REQUIRED_COMPONENT_FIELDS }
